import { ArrowLink } from "@features/atoms/ArrowLink"
import { CloseButton } from "@features/atoms/CloseButton"
import { GlowingBox } from "@features/gfx/GlowingBox"
import { useAppState } from "@hooks/useAppState"
import { cn, tv } from "@utils/tw"
import { useAppData } from "eddev/hooks"
import { Link, useRouterEvents } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useState } from "react"
import { useWindowSize } from "react-use"

export function MainMenuOverlay() {
  const state = useAppState()
  const menuItems = useAppData((d) => d.mainMenu?.menuItems?.nodes ?? [])
  const socialItems = useAppData((d) => d.socialLinks?.menuItems?.nodes ?? [])
  const pillarItems = useAppData((d) => d.pillars?.nodes ?? [])
  const { width } = useWindowSize()
  const isMobile = width < 900

  const [pillarHover, setPillarHover] = useState<null | number>(null)

  useRouterEvents((event) => {
    if (event.type === "clicked") {
      state.closeMenu()
    }
  })

  const styles = createStyles({
    open: state.menuIsOpen,
  })

  return (
    <AnimatePresence initial={false}>
      {state.menuIsOpen && (
        <motion.div data-main-menu-panel className={styles.base()}>
          <motion.div
            className={styles.container()}
            initial={{ opacity: 0, scale: 0.95, borderRadius: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              borderRadius: "var(--border-radius-md)",
              transition: { stiffness: 290, damping: 70 },
            }}
            exit={{ opacity: 0, scale: 0.95, borderRadius: 0 }}
          >
            <CloseButton className="absolute top-3 left-3 md:top-4 md:left-4 z-10" onClick={state.closeMenu} />

            {/* General nav items */}
            <div className={styles.mainNav()}>
              <motion.ul
                initial="hide"
                animate="show"
                exit="hide"
                className="group w-full h-[80%] md:h-auto px-4 text-center type-title-l flex flex-col justify-evenly md:justify-center"
                transition={{ staggerChildren: 0.03 }}
              >
                {menuItems.map((item, i) => (
                  <motion.li key={i} variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
                    <Link
                      href={item.uri}
                      target={item.target}
                      className="md:group-has-[a:hover]:opacity-50 transition-opacity hover:!opacity-100 py-2 md:py-3 block"
                    >
                      {item.label}
                    </Link>
                  </motion.li>
                ))}
              </motion.ul>

              {/* Social items */}
              <ul className={cn(styles.socialNav(), "hidden md:flex")}>
                <li>&copy; Flying Pigs 2025</li>
                {socialItems.map((item, i) => (
                  <li key={i}>
                    <Link href={item.uri} target={item.target}>
                      <ArrowLink type="external">{item.label}</ArrowLink>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.separator()} />

            {/* Pillar nav */}
            <div className={styles.pillarNav()}>
              {pillarItems.map((item, i) => {
                const active = !isMobile && i === pillarHover
                return (
                  <GlowingBox
                    key={i}
                    className={styles.pillarCell()}
                    getParams={() => ({ opacity: active ? 1 : 0, feather: 0 })}
                    alpha={false}
                    onMouseEnter={() => setPillarHover(i)}
                    onMouseLeave={() => setPillarHover(null)}
                  >
                    <Link href={item.uri}>
                      <AnimatePresence mode="popLayout">
                        <motion.div
                          key={active ? "active" : "inactive"}
                          initial={{ opacity: 0, filter: "blur(20px)" }}
                          animate={{ opacity: 1, filter: "none" }}
                          exit={{ opacity: 0, filter: "blur(20px)" }}
                          className="absolute inset-0 flex items-center justify-center flex-col gap-4"
                        >
                          {active ? (
                            <>
                              <div className="type-title-s">{item.subtitle}</div>
                              <div className="type-label-l">
                                <ArrowLink type="right">Learn More</ArrowLink>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="type-label-l">{item.title}</div>
                            </>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </Link>
                  </GlowingBox>
                )
              })}
            </div>

            <div className={cn(styles.separator(), "md:hidden")} />

            {/* Social items */}
            <ul className={cn(styles.socialNav(), "md:hidden")}>
              <li>&copy; 2025</li>
              {socialItems.map((item, i) => (
                <li key={i}>
                  <Link href={item.uri} target={item.target}>
                    <ArrowLink type="external">{item.label}</ArrowLink>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const createStyles = tv({
  base: "fixed inset-0 isolate will-change-transform z-40",
  slots: {
    container: [
      "absolute inset-4 border border-white/20 bg-black rounded-md",
      "flex flex-col justify-between md:!flex-row md:justify-stretch",
    ],
    separator: "flex-none min-w-px min-h-px bg-white/20",
    mainNav: "flex flex-1 flex-col items-center justify-center relative",
    socialNav:
      "flex h-9 md:h-auto md:absolute md:left-0 md:right-0 md:bottom-8 items-center justify-center gap-5 md:gap-6 type-label-l text-white/90 el-a:transition-colors hover:el-a:text-white",
    pillarNav: "flex md:flex-1 justify-end flex-col md:items-stretch md:justify-stretch p-4 gap-4",
    pillarCell:
      "py-5 md:py-0 md:flex-1 md:basis-full md:h-full md:rounded-md md:border md:border-white/5 md:overflow-hidden relative",
  },
  variants: {
    open: {
      true: {},
      false: {
        base: "pointer-events-none",
      },
    },
  },
})
